import {
  ChangeDetectionStrategy,
  Component,
  ErrorHandler,
  Input,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { Auth } from '@freelancer/auth';
import { ProfileViewUser } from '@freelancer/datastore/collections';
import { QuotationFormHelper } from '@freelancer/quotation';
import { ButtonColor } from '@freelancer/ui/button';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { isDefined, toNumber } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { QuotationContextTypeApi } from 'api-typings/quotations/quotations';
import { firstValueFrom } from 'rxjs';

@UntilDestroy({ className: 'DirectQuoteButtonComponent' })
@Component({
  standalone: false,
  selector: 'app-direct-quote-button',
  template: `
    <fl-button
      flTrackingLabel="DirectQuoteButton"
      i18n="Direct quote button"
      display="block"
      [color]="ButtonColor.SECONDARY"
      [busy]="loading"
      [disabled]="loading"
      (click)="handleQuoteClick()"
    >
      Quote
    </fl-button>
  `,
  styleUrls: ['./direct-quote-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DirectQuoteButtonComponent {
  private auth = inject(Auth);
  private quotationHelper = inject(QuotationFormHelper);
  private router = inject(Router);
  private errorHandler = inject(ErrorHandler);
  private toastAlertService = inject(ToastAlertService);

  ButtonColor = ButtonColor;

  @Input() user: ProfileViewUser;

  loading = false;

  handleQuoteClick(): void {
    this.loading = true;

    firstValueFrom(this.auth.getUserId().pipe(untilDestroyed(this))).then(
      async authUserId => {
        const currencyId = this.user.primaryCurrency?.id;
        if (!currencyId) {
          this.openQuotationCreateFailAlert();
          this.loading = false;
          return;
        }

        const draftQuotation = await this.quotationHelper.getOrCreateDraftQuote(
          {
            creatorId: toNumber(authUserId),
            contextId: this.user.id,
            contextType: QuotationContextTypeApi.USER,
            defaultCurrencyId: currencyId,
          },
        );

        if (!isDefined(draftQuotation)) {
          this.loading = false;
          this.errorHandler.handleError(
            `Could not share quote to client ${this.user.id}.`,
          );
          this.openQuotationCreateFailAlert();
          return;
        }

        return this.router.navigate(
          [`quotation/${draftQuotation.quotationUid}/create`],
          {
            queryParams: { from: encodeURIComponent(this.router.url) },
          },
        );
      },
    );
  }

  openQuotationCreateFailAlert(): void {
    this.toastAlertService.open({
      id: `quotation-create-fail-alert-${this.user.id}`,
      timeout: 5000,
      type: ToastAlertType.ERROR,
      message: $localize`Oops, something went wrong. There was a problem with creating a quote. Please try again. If the problem persists, please get in touch with support.`,
      action: {
        type: 'link',
        text: $localize`Contact Support`,
        url: '/support',
        trackingSection: 'SearchUsersResultCard',
        trackingLabel: 'LinkToSupportError',
      },
    });
  }
}
